import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Space } from 'antd';
import MLModel from '../../../ml_models/models/MLModel';
import { PromptTemplate } from '../../../prompt_templates/types';
import CustomBlockCard from './CustomBlockCard';
import BlockTypeSelector from './BlockTypeSelector';
import SmartBlockConfig from './SmartBlockConfig';
import InterpretationBlockPipeline from './InterpretationBlockPipeline';

export interface SmartBlock {
    type: 'SMART_BLOCK';
    model_id: string;
}

export interface ImagePipelineStep {
    type: 'image';
    model_id: string;
    prompt_id?: string;
    system_prompt_id?: string;
    enable_preprocessing?: boolean;
}

export interface TextPipelineStep {
    type: 'text';
    model_id: string;
    prompt_id?: string;
    system_prompt_id?: string;
}

export interface RemotePipelineStep {
    type: 'remote';
    url?: string;
}

export type PipelineStep = ImagePipelineStep | TextPipelineStep | RemotePipelineStep;

export interface InterpretationBlock {
    type: 'INTERPRETATION_BLOCK';
    pipeline: PipelineStep[];
}

export type CustomBlockType = SmartBlock | InterpretationBlock;

export const hasPipeline = (block: CustomBlockType): block is CustomBlockType & { pipeline: PipelineStep[] } => {
    return 'pipeline' in block;
};

interface Store {
    mlModels: MLModel[];
    promptTemplates: PromptTemplate[];
    isLoadingModels: boolean;
    isLoadingPrompts: boolean;
}

interface CustomBlockProps {
    block: CustomBlockType;
    index: number;
    disabled: boolean;
    store: Store;
    onRemoveBlock: (index: number) => void;
    onBlockTypeChange: (index: number, newType: 'SMART_BLOCK' | 'INTERPRETATION_BLOCK') => void;
    onSmartBlockModelChange: (index: number, modelId: string) => void;
    onAddPipelineStep: (blockIndex: number) => void;
    onRemovePipelineStep: (blockIndex: number, stepIndex: number) => void;
    onPipelineStepTypeChange: (blockIndex: number, stepIndex: number, newType: 'image' | 'text' | 'remote') => void;
    onPipelineStepFieldChange: (
        blockIndex: number,
        stepIndex: number,
        field: string,
        fieldValue: string | boolean | undefined
    ) => void;
    onMovePipelineStep: (blockIndex: number, dragIndex: number, hoverIndex: number) => void;
}

const CustomBlock: React.FC<CustomBlockProps> = ({
    block,
    index,
    disabled,
    store,
    onRemoveBlock,
    onBlockTypeChange,
    onSmartBlockModelChange,
    onAddPipelineStep,
    onRemovePipelineStep,
    onPipelineStepTypeChange,
    onPipelineStepFieldChange,
    onMovePipelineStep
}) => {
    return (
        <CustomBlockCard index={index} disabled={disabled} onRemove={() => onRemoveBlock(index)}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
                <BlockTypeSelector
                    value={block.type}
                    onChange={val => onBlockTypeChange(index, val)}
                    disabled={disabled}
                />

                {block.type === 'SMART_BLOCK' && (
                    <SmartBlockConfig
                        modelId={block.model_id}
                        models={store.mlModels}
                        isLoadingModels={store.isLoadingModels}
                        disabled={disabled}
                        onModelChange={val => onSmartBlockModelChange(index, val)}
                    />
                )}

                {block.type === 'INTERPRETATION_BLOCK' && (
                    <InterpretationBlockPipeline
                        pipeline={block.pipeline}
                        models={store.mlModels}
                        promptTemplates={store.promptTemplates}
                        isLoadingModels={store.isLoadingModels}
                        isLoadingPrompts={store.isLoadingPrompts}
                        disabled={disabled}
                        onAddStep={() => onAddPipelineStep(index)}
                        onRemoveStep={stepIndex => onRemovePipelineStep(index, stepIndex)}
                        onStepTypeChange={(stepIndex, newType) => onPipelineStepTypeChange(index, stepIndex, newType)}
                        onStepFieldChange={(stepIndex, field, value) =>
                            onPipelineStepFieldChange(index, stepIndex, field, value)
                        }
                        onMoveStep={(dragIndex, hoverIndex) => onMovePipelineStep(index, dragIndex, hoverIndex)}
                    />
                )}
            </Space>
        </CustomBlockCard>
    );
};

export default observer(CustomBlock);
